import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { jwtDecode } from 'jwt-decode';
import logger from '../../utils/logger';
import { authStorage } from '../../utils/storage';
import Wordmark from './Wordmark';
import '../styles/general/Login.css';

const IS_STAGING = process.env.REACT_APP_STAGING === 'true';

const LoginEmail = () => {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [, setHasLoggedIn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, setAuth } = useAuth();

  const initialAuth = useRef({ pathname: location.pathname, isAuthenticated: auth.isAuthenticated, navigate });

  // Check for existing valid token on mount
  useEffect(() => {
    const { pathname, isAuthenticated, navigate } = initialAuth.current;
    // Only redirect if we're actually on the login page
    if (pathname !== '/login') {
      return;
    }
    
    // Don't check if already authenticated
    if (isAuthenticated) {
      return;
    }
    
    // Only check token on initial mount
    const checkExistingToken = () => {
      const token = authStorage.getToken();
      if (token) {
        try {
          const decodedToken = jwtDecode(token);
          // Check if token is still valid (not expired)
          const currentTime = Date.now() / 1000;
          if (decodedToken.exp && decodedToken.exp > currentTime) {
            logger.log('LoginEmail', 'Valid token found, redirecting to metrics');
            navigate('/metrics', { replace: true });
          } else {
            // Token expired, clear it
            logger.log('LoginEmail', 'Token expired, clearing session');
            authStorage.clearAuthData();
          }
        } catch (error) {
          logger.error('LoginEmail', 'Error decoding token', error);
          authStorage.clearAuthData();
        }
      }
    };

    // Only run on mount, not on updates
    checkExistingToken();
  }, []); // Remove dependencies to run only on mount

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!showCodeInput) {
      // Request login code
      if (!validateEmail(email)) {
        setMessage('Please enter a valid email address');
        setIsSuccess(false);
        return;
      }

      setIsLoading(true);
      setMessage('');

      try {
        const response = await fetch('/api/auth/request-login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: email.toLowerCase().trim() }),
        });

        const data = await response.json();

        if (response.ok) {
          setIsSuccess(true);
          setMessage('Login code sent! Please check your email.');
          setShowCodeInput(true);
        } else {
          setIsSuccess(false);
          setMessage(data.message || 'Unable to send login email. Please try again.');
        }
      } catch (error) {
        setIsSuccess(false);
        setMessage('Unable to connect. Please try again later.');
        console.error('Login error:', error);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Verify code
      if (!code || code.length !== 6) {
        setMessage('Please enter the 6-character code from your email');
        setIsSuccess(false);
        return;
      }

      setIsLoading(true);
      setMessage('');

      try {
        const response = await fetch('/api/auth/verify-code', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ 
            email: email.toLowerCase().trim(),
            code: code 
          }),
        });

        const data = await response.json();

        if (response.ok) {
          // Store auth data using the storage utility
          authStorage.setAuthData(data);
          
          // Update auth context
          try {
            const decodedUser = jwtDecode(data.token);
            logger.log('LoginEmail', 'Updating auth context', { username: decodedUser.username });
            setAuth({ 
              token: data.token, 
              isAuthenticated: true, 
              user: decodedUser 
            });
          } catch (error) {
            logger.error('LoginEmail', 'Error decoding token', error);
          }
          
          setIsSuccess(true);
          setMessage('Success! Redirecting...');
          setHasLoggedIn(true);
          
          // Force a small delay to ensure auth context updates
          setTimeout(() => {
            logger.log('LoginEmail', 'About to navigate to metrics');
            navigate('/metrics', { replace: true });
          }, 100);
        } else {
          setIsSuccess(false);
          setMessage(data.message || 'Invalid code. Please try again.');
        }
      } catch (error) {
        setIsSuccess(false);
        setMessage('Unable to connect. Please try again later.');
        console.error('Verification error:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">

        {/* Brand */}
        <div className="login-brand">
          <Wordmark size={36} />
          <div className="login-brand-name">DATAHOUSE</div>
        </div>

        <form onSubmit={handleSubmit}>
          <p className="login-heading">Sign in to your account</p>

          {!showCodeInput ? (
            <>
              <div className="login-field">
                <label className="login-label" htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  className="login-input"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  autoFocus
                  required
                />
              </div>

              <button type="submit" className="login-submit" disabled={isLoading || !email}>
                {isLoading && <span className="login-spinner" />}
                {isLoading ? 'Sending…' : 'Send sign-in code'}
              </button>
            </>
          ) : (
            <>
              <p style={{ fontSize: 13, color: 'var(--fg-2)', textAlign: 'center', margin: '0 0 18px' }}>
                We sent a code to <strong style={{ color: 'var(--fg-1)' }}>{email}</strong>
              </p>

              <div className="login-field">
                <label className="login-label" htmlFor="code">Verification code</label>
                <input
                  id="code"
                  type="text"
                  className="login-input"
                  placeholder="——————"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))}
                  disabled={isLoading}
                  style={{ textAlign: 'center', fontSize: 22, letterSpacing: '0.4em', fontFamily: 'var(--font-mono)' }}
                  autoComplete="one-time-code"
                  autoFocus
                  required
                />
              </div>

              <button type="submit" className="login-submit" disabled={isLoading || !code || code.length !== 6}>
                {isLoading && <span className="login-spinner" />}
                {isLoading ? 'Verifying…' : 'Verify code'}
              </button>

              <div style={{ marginTop: 14, textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowCodeInput(false);
                    setCode('');
                    setMessage('');
                    setIsSuccess(false);
                  }}
                  style={{ background: 'none', border: 'none', color: 'var(--fg-3)', fontSize: 12, cursor: 'pointer', fontFamily: 'var(--font-sans)', letterSpacing: '0.03em', textDecoration: 'underline' }}
                >
                  Use a different email
                </button>
              </div>
            </>
          )}

          {message && (
            isSuccess ? (
              <div style={{ marginTop: 12, padding: '9px 12px', background: 'var(--color-success-soft)', border: '1px solid rgba(46,160,107,0.20)', borderRadius: 'var(--radius-sm)', color: '#1b6b45', fontSize: 13, textAlign: 'center' }}>
                {message}
              </div>
            ) : (
              <div className="login-error">{message}</div>
            )
          )}
        </form>

        {/* Footer */}
        <div className="login-footer">
          <span className="login-footer-dot" />
          <span className="login-footer-text">
            Datahouse · Dailey OS{IS_STAGING ? ' · Staging' : ''}
          </span>
        </div>

      </div>
    </div>
  );
};

export default LoginEmail;